import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Send, Check, FileText, Coins, Wallet } from "@semo/icons";
import { toast } from "sonner";
import {
  ButtonShadcn as Button,
  CheckCard,
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  cn,
} from "@semo/shared-ui";
import type {
  QuoteItemDto,
  QuoteRequestListItemDto,
  SupplierInfoDto,
  QuoteMessageResponseDto,
} from "@semo/api-contracts";
import { QuoteHeader } from "../quotes/QuoteHeader";
import { QuoteRecipientAmount } from "../quotes/QuoteRecipientAmount";
import { QuoteProviderInfo } from "../quotes/QuoteProviderInfo";
import { QuoteFooter } from "../quotes/QuoteFooter";
import { QuoteRequestItemDtosTable } from "../quotes/QuoteItemsTable";
import { QuoteSummary } from "../quotes/QuoteSummary";
import {
  formatDateKorean,
  transformApiItemToUiItem,
  combineSpecs,
  type ApiItem,
} from "@semo/shared-utils";
import { convertNumberToKorean } from "@semo/shared-utils";
import { CatalogUpdateModal } from "./CatalogUpdateModal";
import { parseUnitPriceInput } from "./quote-price-input";
import {
  useQuoteForm,
  useQuoteCalculations,
  DEFAULT_VAT_MODE,
  type QuoteItem,
  type QuoteFormFile,
  type QuoteVatMode,
} from "../../features/quotes";
import { QuoteRequestDetailDto } from "@semo/shared-ui";
import { getPaymentChannelLabel } from "@semo/shared-ui";

// Helper type guard to safely check if an unknown value is an ApiItem
function isApiItemObj(item: unknown): item is ApiItem {
  return typeof item === "object" && item !== null;
}

/**
 * Discriminated union props for type-safe dual usage
 * - create mode: Uses QuoteRequestListItemDto or QuoteRequestDetailDto (buyer's request)
 *   - QuoteRequestListItemDto: From list endpoints (includes responseCount, hasResponded)
 *   - QuoteRequestDetailDto: From detail endpoints (used by embed page)
 * - edit mode: Uses quoteData (supplier's existing response with version info)
 */
type BaseQuoteModalProps =
  | {
      mode: "create";
      quoteRequest: QuoteRequestListItemDto | QuoteRequestDetailDto;
      initialUnitPrice?: number;
      supplierInfo?: SupplierInfoDto | null;
      supplierId?: string; // ✨ 자동완성 기능을 위한 공급사 ID
      supplierPriceMap?: Map<string, { supplierPrice: number | null }>; // ✨ 자동 단가 조회 결과
      onClose: () => void;
      onSubmit: (
        items: QuoteItem[],
        notes: string,
        files: QuoteFormFile[],
        vatMode: QuoteVatMode,
      ) => Promise<void>;
      isSubmitting?: boolean;
      variant?: "modal" | "full"; // ✨ Display variant: 'modal' (default) or 'full' (embed page)
      hideCloseButton?: boolean; // 임베드(iframe)에서 부모 모달이 닫기를 제공할 때 자체 X 숨김
    }
  | {
      mode: "edit";
      quoteData: NonNullable<QuoteMessageResponseDto["quoteData"]> & {
        buyerName: string;
        categoryType?: string;
      };
      supplierInfo?: SupplierInfoDto | null;
      supplierId?: string; // ✨ 자동완성 기능을 위한 공급사 ID
      supplierPriceMap?: Map<string, { supplierPrice: number | null }>; // ✨ 자동 단가 조회 결과
      onClose: () => void;
      onSubmit: (
        items: QuoteItem[],
        notes: string,
        files: QuoteFormFile[],
        vatMode: QuoteVatMode,
      ) => Promise<void>;
      isSubmitting?: boolean;
      variant?: "modal" | "full"; // ✨ Display variant: 'modal' (default) or 'full' (embed page)
      hideCloseButton?: boolean; // 임베드(iframe)에서 부모 모달이 닫기를 제공할 때 자체 X 숨김
    };

/**
 * BaseQuoteModal - Refactored using Feature-Based Architecture
 *
 * Clean Architecture Layers:
 * - Uses presentation hooks (useQuoteForm, useQuoteCalculations)
 * - NO direct API calls (delegates to parent via onSubmit)
 * - NO business logic (uses domain calculators via hooks)
 * - Pure presentational component
 */
export function BaseQuoteModal(props: BaseQuoteModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const variant = props.variant || "modal"; // Default to modal behavior
  const [agreedToSpecs, setAgreedToSpecs] = useState(false);
  const [agreedToFees, setAgreedToFees] = useState(false);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [changedItems, setChangedItems] = useState<
    { name: string; oldPrice: number; newPrice: number }[]
  >([]);

  // ✅ NEW: Initialize items from props
  const initialItems = useMemo((): QuoteItem[] => {
    if (props.mode === "create") {
      const requestItems =
        "items" in props.quoteRequest ? props.quoteRequest.items : [];

      // 물품 외 카테고리에서 items가 없으면 프로젝트명으로 기본 항목 생성
      if (!requestItems || requestItems.length === 0) {
        const projectName = props.quoteRequest.projectName || "프로젝트";
        return [
          {
            itemName: projectName,
            quantity: 1,
            unitPrice: 0,
          },
        ];
      }

      return requestItems.map((item: unknown) => {
        if (!isApiItemObj(item)) {
          return { itemName: "", quantity: 0, unitPrice: 0, specification: "" };
        }

        const quantity = Number(item.quantity ?? 0);

        // ✨ Priority: Supplier price > URL param > Reference price > 0
        const itemId = item.itemId ?? undefined;

        let supplierPrice: number | null | undefined = null;
        if (props.supplierPriceMap) {
          // Try lookup by itemId (UUID) - stricter matching
          if (itemId && props.supplierPriceMap.has(itemId)) {
            supplierPrice = props.supplierPriceMap.get(itemId)?.supplierPrice;
          }
        }

        // ✨ Priority: Supplier price > URL param > explicit unitPrice > amount/quantity 파생 > 0
        //
        // External quote request items(QuoteRequestItemResponseDto)에는 unitPrice 필드가 없고
        // amount(총액)와 quantity 만 담긴다. supplier 가 자기 공급가를 등록하지 않았고 URL
        // 힌트도 없으면 buyer 가 기재한 amount 를 참조단가로 역산해 프리필한다 —
        // 그래야 vendor 가 매번 0 에서 다시 입력하지 않는다.
        const explicitUnitPrice = Number(item.unitPrice ?? 0);
        const derivedUnitPrice =
          explicitUnitPrice > 0
            ? explicitUnitPrice
            : quantity > 0 && item.amount
              ? Math.round(Number(item.amount) / quantity)
              : 0;
        const unitPrice =
          supplierPrice ?? props.initialUnitPrice ?? derivedUnitPrice;

        // ✨ Create combined spec string for initial value so editing is seamless
        const uiItem = transformApiItemToUiItem(item);

        return {
          itemName: uiItem.name,
          quantity,
          unitPrice,
          unit: uiItem.unit || "개", // ✨ Synchronized unit
          itemId, // Preserved for catalog sync
          specification: uiItem.specification || "", // Use combined spec for editing
        };
      });
    } else {
      // edit mode: Convert DTO to domain model
      // quoteData items are already QuoteItemDto[]
      return (props.quoteData.items || []).map((item) => {
        const uiItem = transformApiItemToUiItem(item);
        return {
          itemName: uiItem.name,
          quantity: uiItem.quantity,
          unit: uiItem.unit || "개", // ✨ Synchronized unit
          unitPrice: item.unitPrice || 0,
          specification: uiItem.specification || "", // Use combined spec for editing
          // itemId: (item as any).itemId, // Not currently in QuoteItemDto but might be needed
        };
      });
    }
  }, [
    props.mode,
    "initialUnitPrice" in props ? props.initialUnitPrice : undefined,
    props.supplierPriceMap,
    props.mode === "edit" ? JSON.stringify(props.quoteData.items) : null,
  ]);

  const initialNotes =
    props.mode === "create" ? "" : (props.quoteData.description ?? "");

  // ✅ NEW: Initialize files from props
  const initialFiles = useMemo((): QuoteFormFile[] => {
    if (props.mode === "create" || !props.quoteData.attachments) {
      return [];
    }

    // Map DTO attachments to QuoteFormFile
    return props.quoteData.attachments.map((att) => ({
      id: crypto.randomUUID(), // DTO might not have ID, generate one for UI
      fileName: att.fileName ?? "unknown",
      fileSize: formatFileSize(att.fileSize ?? 0),
      url: att.url,
      filePath: att.filePath,
      isExisting: true,
    }));
  }, [
    props.mode,
    props.mode === "edit" ? JSON.stringify(props.quoteData.attachments) : null,
  ]);

  // ✅ NEW: Use presentation hooks for state management
  const {
    items,
    notes,
    files,
    updateItem,
    addItem,
    removeItem,
    setNotes,
    addFile,
    removeFile,
    resetForm,
  } = useQuoteForm({ initialItems, initialNotes, initialFiles });

  // Update form when initialItems changes
  useEffect(() => {
    resetForm(initialItems, initialNotes, initialFiles);
  }, [initialItems, initialNotes, initialFiles, resetForm]);

  // 단가 입력 축 — 부가세 별도(공급가액) / 부가세 포함.
  //
  // 부가세 포함으로 거래하는 공급사가 공급가액 단가를 직접 나눠 넣으면(16,000 ÷ 1.1 = 14,545)
  // 버린 소수가 수량만큼 증폭돼 총액이 어긋난다(실측 116개에서 58원 부족). 포함가로 받아
  // 합계에서 역산하면 그 오차가 없다.
  //
  // 재견적은 **이전 견적서의 축을 그대로 이어받는다.** 이어받지 않으면 포함가로 쓴 견적을
  // 고치러 열었을 때 축만 «별도» 로 풀린다 — 같은 단가가 공급가액으로 다시 읽혀, 공급사가
  // 단가를 건드린 적이 없는데도 총액이 10% 뛴 견적서가 나간다.
  const incomingVatMode: QuoteVatMode =
    props.mode === "edit" && props.quoteData.taxType === "INCLUSIVE"
      ? "INCLUSIVE"
      : DEFAULT_VAT_MODE;
  const [vatMode, setVatMode] = useState<QuoteVatMode>(incomingVatMode);

  // 품목과 같은 이유로 한 번 더 맞춘다 — 창이 뜬 뒤에 견적 데이터가 도착하면 처음 값만으로는
  // 축이 안 따라온다. 넘어온 축이 실제로 바뀔 때만 도므로 공급사가 손으로 바꾼 축은 덮지 않는다.
  useEffect(() => {
    setVatMode(incomingVatMode);
  }, [incomingVatMode]);

  const isVatIncluded = vatMode === "INCLUSIVE";

  // ✅ NEW: Use presentation hook for calculations (delegates to domain layer)
  const { total, vat, subtotal } = useQuoteCalculations(items, vatMode);

  const totalKorean = convertNumberToKorean(total);

  // Extract data based on mode
  // 수신자는 기관명이 내려오면 기관명, 아니면 담당자명. organizationName 은 백엔드가
  // 공고 공개 여부(isPublic)로 가른다 — 비공개 공고·기관명 없는 구매자는 null 로 온다.
  // ⚠️ projectName 을 폴백에 넣지 않는다 — 기관명·담당자명이 둘 다 비면(익명 공고 등)
  // «시약 및 전산용품 구매 귀중» 처럼 공고명이 수신처에 찍히는 사고가 났다.
  // 하드코딩 목업 잔재("(주)미래기술")도 같은 이유로 제거 — 모르면 중립 표기가 맞다.
  const customerName =
    props.mode === "create"
      ? (props.quoteRequest.organizationName ??
        props.quoteRequest.managerName ??
        "발주처")
      : props.quoteData.buyerName;

  const categoryType =
    props.mode === "create"
      ? props.quoteRequest.categoryType
      : (props.quoteData.categoryType ?? "product");

  /**
   * 공고에서 발주기관이 고른 희망 결제수단. **단가를 매기기 전에 보여야 한다** —
   * 카드냐 현금이냐로 가격이 갈리기 때문이다. 이게 안 보이면 나중에 발주기관이
   * 「업체와 상의하세요」 안내를 받고 연락해 와도, 공급사는 자기가 무슨 기준으로
   * 견적을 냈는지 모른다.
   *
   * 수정(edit) 모드에는 공고 정보가 없어 표시하지 않는다.
   * null(신설 이전 공고)이면 라벨이 null 이라 칩 자체를 안 그린다.
   */
  const paymentChannelLabel =
    props.mode === "create"
      ? getPaymentChannelLabel(props.quoteRequest.preferredPaymentChannel)
      : null;

  // ✅ SIMPLIFIED: Item handlers delegate to presentation hook
  const handleItemChange = (
    index: number,
    field: keyof QuoteItemDto | "specification",
    value: unknown,
  ) => {
    if (field === "specification") {
      updateItem(index, "specification", value as string);
    } else if (field === "name") {
      updateItem(index, "itemName", value as string);
    } else if (field === "quantity") {
      const quantity =
        typeof value === "string"
          ? parseFloat(value.replace(/,/g, "")) || 0
          : Number(value) || 0;
      updateItem(index, "quantity", quantity);
    } else if (field === "unitPrice") {
      updateItem(index, "unitPrice", value as number);
    } else if (field === "unit") {
      updateItem(index, "unit", value as string);
    }
  };
  const handlePriceChange = (index: number, value: string) => {
    updateItem(index, "unitPrice", parseUnitPriceInput(value));
  };

  // ✨ NEW: Autocomplete selection handler
  const handleAutocompleteSelect = (
    index: number,
    itemName: string,
    unitPrice: number,
    itemId: string,
    spec1?: string | null,
    spec2?: string | null,
    spec3?: string | null,
  ) => {
    updateItem(index, "itemName", itemName);
    updateItem(index, "unitPrice", unitPrice);
    updateItem(index, "itemId", itemId);

    // ✨ Automatically combine and apply spec strings if they exist
    const specification = combineSpecs({ spec1, spec2, spec3 });
    if (specification && specification !== "-") {
      updateItem(index, "specification", specification);
    }
  };

  // ✅ SIMPLIFIED: File handlers delegate to presentation hook
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;

    Array.from(selectedFiles).forEach((file) => addFile(file));
  };

  // UI 조건 설정
  const isEditable = props.mode === "edit" || props.mode === "create"; // Both modes need to be editable initially
  const showSpecValue = categoryType !== "construction";
  const isProjectBasedValue = categoryType === "construction";

  // ✅ SIMPLIFIED: Submission delegates to parent (NO API calls)
  const handleSave = async () => {
    // Validation: Check if any item has 0 or missing unit price
    const hasInvalidPrice = items.some(
      (item) => !item.unitPrice || item.unitPrice <= 0,
    );

    if (hasInvalidPrice) {
      // 문구의 단가 축은 지금 고른 입력 축을 따른다 — 포함가로 쓰는 중에 «VAT 별도» 를
      // 입력하라고 하면 공급사가 축을 바꿔야 하는 줄 안다(V-15).
      const priceLabel = isVatIncluded ? "단가(VAT 포함)" : "단가(VAT 별도)";
      toast.error(`${priceLabel} 입력 확인`, {
        description: `모든 항목의 ${priceLabel}를 입력해주세요. (0원 불가)`,
        duration: 3000,
      });
      return;
    }

    // ✨ Validation: Check consents (create mode only)
    if (props.mode === "create") {
      if (!agreedToSpecs) {
        toast.error("필수 동의 필요", {
          description: "규격 및 제반 내용 확인 동의에 체크해주세요.",
          duration: 3000,
        });
        return;
      }
      if (!agreedToFees) {
        toast.error("필수 동의 필요", {
          description: "플랫폼 수수료 부과 확인 동의에 체크해주세요.",
          duration: 3000,
        });
        return;
      }
    }

    // ✨ NEW: Catalog Price Change Check
    const changes = items
      .filter((item) => {
        if (!item.itemId || !props.supplierPriceMap) return false;
        const catalogPrice = props.supplierPriceMap.get(
          item.itemId,
        )?.supplierPrice;
        return catalogPrice && catalogPrice !== item.unitPrice;
      })
      .map((item) => ({
        name: item.itemName,
        oldPrice: props.supplierPriceMap!.get(item.itemId!)!.supplierPrice!,
        newPrice: item.unitPrice,
      }));

    if (changes.length > 0) {
      setChangedItems(changes);
      setShowCatalogModal(true);
      return;
    }

    // If no changes, proceed with default (auto-update for first-time is handled by backend)
    await submitWithCatalogChoice(0);
  };

  const submitWithCatalogChoice = async (updateChoice: 0 | 1) => {
    try {
      const finalItems = items.map((item) => {
        const hasCatalogPrice =
          item.itemId &&
          props.supplierPriceMap?.get(item.itemId)?.supplierPrice;
        return {
          ...item,
          updateCatalog: hasCatalogPrice ? updateChoice : undefined,
        };
      });

      await props.onSubmit(finalItems, notes, files, vatMode);
      props.onClose();
    } catch (error) {
      console.error("[BaseQuoteModal] Submission failed:", error);
    }
  };

  const handleConfirmUpdate = () => {
    setShowCatalogModal(false);
    submitWithCatalogChoice(1);
  };

  const handleCancelUpdate = () => {
    setShowCatalogModal(false);
    submitWithCatalogChoice(0);
  };

  // 날짜 포맷
  const date = formatDateKorean(new Date());

  const isSubmitting = props.isSubmitting ?? false;
  const submitButtonLabel = props.mode === "create" ? "견적 발송" : "수정 완료";
  const submitButtonLabelShort = props.mode === "create" ? "발송" : "완료";
  const submitButtonLoadingLabel =
    props.mode === "create" ? "발송 중..." : "저장 중...";
  const submitButtonLoadingLabelShort = "...";
  const submitIcon =
    props.mode === "create" ? (
      <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
    ) : (
      <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
    );

  // Convert QuoteItem to QuoteItemDto for table display
  // Preserve spec1, spec2, spec3 from original request items for proper spec rendering
  const displayItems: QuoteItemDto[] = items.map((item) => {
    return {
      name: item.itemName,
      quantity: item.quantity,
      unit: item.unit || "개",
      unitPrice: item.unitPrice,
      amount: item.quantity * item.unitPrice,
      itemId: item.itemId, // Pass through
      // Map combined specification back to spec1 for display purposes. Clear spec2/spec3 to prevent duplication.
      spec1: item.specification,
      spec2: undefined,
      spec3: undefined,
    };
  });

  // Modal variant styles (default)
  const modalInnerClass =
    "bg-white  w-full max-w-7xl h-[95vh] rounded-lg sm:rounded-xl shadow-2xl flex flex-col overflow-hidden";

  // Full page variant styles (embed)
  // Changed to fixed positioning to ensure it overlays the chat window in full-page mode
  const fullInnerClass = "w-full h-full flex flex-col bg-white overflow-hidden";

  // Animation props based on variant
  const animationProps =
    variant === "modal"
      ? {
          initial: { opacity: 0, scale: 0.95, y: 20 },
          animate: { opacity: 1, scale: 1, y: 0 },
          exit: { opacity: 0, scale: 0.95, y: 20 },
        }
      : {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          exit: { opacity: 0 },
        };

  const containerContent = (
    <motion.div
      {...animationProps}
      className={
        variant === "modal"
          ? cn(
              modalInnerClass,
              "h-full w-full max-w-none rounded-none shadow-none",
            )
          : fullInnerClass
      }
      onClick={(e) => e.stopPropagation()} // Prevent modal close when clicking inside
    >
      {/* Header Toolbar */}
      <div className="h-[56px] sm:h-[60px] border-b border-slate-200  flex items-center justify-between px-3 sm:px-6 bg-slate-50  shrink-0">
        <h2 className="text-sm sm:text-base font-semibold text-slate-900 ">
          {props.mode === "create"
            ? "견적서 작성"
            : `견적서 수정 v${props.quoteData.versionNumber || 1}`}
          {paymentChannelLabel && (
            <span className="ml-2 inline-flex items-center gap-1 rounded-md bg-primary/5 px-2 py-0.5 text-2xs sm:text-xs font-semibold text-primary-text align-middle">
              <Wallet className="w-3 h-3" />
              {paymentChannelLabel} 결제 희망
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          {props.mode === "edit" && (
            <Button
              onClick={handleSave}
              disabled={isSubmitting}
              className="flex justify-center items-center h-8 sm:h-9 gap-1.5 bg-primary hover:bg-primary-hover disabled:bg-primary/50 disabled:cursor-not-allowed text-primary-foreground text-xs sm:text-sm px-4 transition-colors"
            >
              {isSubmitting ? (
                <>
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span className="hidden sm:inline">
                    {submitButtonLoadingLabel}
                  </span>
                  <span className="sm:hidden">
                    {submitButtonLoadingLabelShort}
                  </span>
                </>
              ) : (
                <>
                  {submitIcon}
                  <span className="hidden sm:inline">{submitButtonLabel}</span>
                  <span className="sm:hidden">{submitButtonLabelShort}</span>
                </>
              )}
            </Button>
          )}
          {!props.hideCloseButton && (
            <>
              <div className="w-px h-4 bg-slate-300  mx-1" />
              <button
                onClick={props.onClose}
                className="p-1.5 sm:p-2 hover:bg-slate-200 :bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5 text-slate-500 " />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Document Content */}
      <div className="flex-1 overflow-y-auto bg-[#f2f5f9] p-2 sm:p-4 md:p-8 custom-scrollbar pb-24 md:pb-8">
        <div className="mx-auto max-w-[210mm] min-h-0 md:min-h-[297mm] w-full bg-white border border-[#e8edf3] rounded-[8px] sm:rounded-[14px] shadow-[0_16px_50px_rgba(17,24,39,.08)] p-4 sm:p-6 md:p-[40px] overflow-hidden relative flex flex-col">
          {/* Header Area */}
          <QuoteHeader date={date} />

          {/* Recipient & Amount Line */}
          <QuoteRecipientAmount
            customerName={customerName}
            totalAmount={total}
            totalAmountKorean={totalKorean}
          />

          {/* Provider Info */}
          <QuoteProviderInfo
            provider={
              props.supplierInfo
                ? {
                    registrationNumber: props.supplierInfo.businessNumber || "",
                    companyName: props.supplierInfo.companyName || "",
                    representative: props.supplierInfo.ceoName || "",
                    addressFull: props.supplierInfo.addressFull || "",
                    businessType: props.supplierInfo.businessType || "",
                    managerName: props.supplierInfo.managerName || "",
                    managerPhone: props.supplierInfo.managerPhone || "",
                    companySealUrl:
                      props.supplierInfo.companySealUrl ?? undefined,
                  }
                : undefined
            }
          />

          {/* Items Table — 단가 축 토글은 header 아래에 붙는다 */}
          <QuoteRequestItemDtosTable
            items={displayItems}
            showSpec={showSpecValue}
            isProjectBased={isProjectBasedValue}
            isEditable={isEditable}
            unitPriceLabel={isVatIncluded ? "단가(VAT 포함)" : "단가(VAT 별도)"}
            vatMode={vatMode}
            onVatModeChange={setVatMode}
            enableAutocomplete={!!props.supplierId} // ✨ supplierId가 있으면 자동완성 활성화
            supplierId={props.supplierId}
            supplierPriceMap={props.supplierPriceMap} // Pass map for hints
            onItemChange={handleItemChange}
            onPriceChange={handlePriceChange}
            onAddItem={addItem}
            onDeleteItem={removeItem}
            onAutocompleteSelect={handleAutocompleteSelect}
          />

          {/* Summary Section */}
          <QuoteSummary subTotal={subtotal} vat={vat} total={total} />

          {/* Notices & Attachments */}
          <QuoteFooter
            notes={notes}
            files={files.map((f) => ({
              id: f.id,
              fileName: f.fileName,
              fileSize: f.fileSize, // Pass formatted string directly
              url: f.url,
              file: f.file, // Optional
            }))}
            onAddFile={() => fileInputRef.current?.click()}
            onRemoveFile={(id) => removeFile(id)}
            onFileChange={handleFileChange}
            fileInputRef={fileInputRef}
            onNotesChange={setNotes}
            uploadError={null}
          />

          {/* ✨ Consents Section (Create Mode Only) */}
          {props.mode === "create" && (
            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="font-semibold text-gray-800 mb-2 text-xs sm:text-sm md:text-sm flex items-center gap-2 shrink-0 whitespace-nowrap">
                <span className="w-1 h-3 sm:h-4 bg-primary rounded-sm block shrink-0"></span>
                제출 동의 및 확인
              </div>
              <div className="space-y-3">
                {/* 1. Specs Agreement Card */}
                <CheckCard
                  checked={agreedToSpecs}
                  onCheckedChange={setAgreedToSpecs}
                  title="[필수] 본 견적의 규격·요청사항 등 제반 내용을 충분히 확인했으며, 이에 동의합니다."
                  description="(동일 프로젝트에 대한 동의는 최초 1회로 갈음합니다.)"
                  icon={undefined}
                  footer={
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="text-xs text-primary-text hover:text-primary-hover hover:underline font-medium inline-flex items-center gap-0.5">
                          상세보기
                          <span className="text-2xs">›</span>
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>제반 사항 동의</AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-600">
                            본 견적 제출 전, 요청자가 제시한 모든 규격, 수량,
                            납기일, 특수 조건 등을 상세히 확인하였음을
                            확약합니다.
                            <br />
                            <br />
                            단순 변심이나 규격 미확인으로 인한 추후 계약 파기 및
                            분쟁 발생 시, 공급사 신뢰도 하락 등의 불이익이
                            발생할 수 있습니다.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogAction>확인</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  }
                />

                {/* 2. Fees Agreement Card */}
                <CheckCard
                  checked={agreedToFees}
                  onCheckedChange={setAgreedToFees}
                  title="[필수] 계약 성사 시 최종 합계 금액 기준으로 플랫폼 수수료(5%, 부가세 별도)가 부과됨을 확인했으며, 이에 동의합니다."
                  description="최종 합계 금액의 5%(부가세 별도 · 부가세 포함 5.5%)가 플랫폼 수수료로 부과됩니다."
                  icon={undefined}
                  footer={
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="text-xs text-primary-text hover:text-primary-hover hover:underline font-medium inline-flex items-center gap-0.5">
                          수수료 안내
                          <span className="text-2xs">›</span>
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            플랫폼 수수료 안내
                          </AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-600">
                            본 플랫폼을 통해 계약이 성사될 경우, 최종 합계
                            금액의 5%(부가세 별도)가 플랫폼 수수료로
                            부과됩니다. 부가세 10%를 더하면 최종 합계 금액의
                            5.5%이며, 수수료 정산 화면에는 이 부가세 포함
                            금액이 청구됩니다.
                            <br />
                            <br />
                            견적가는 이를 고려하여 산정해주시기 바랍니다.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogAction>확인</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  }
                />

                {/* 3. 카탈로그 자동 등록 안내 — 서버(CatalogSyncService)는 견적 품목 중 내 단가가
                    아직 없는 카탈로그 품목을 견적 단가로 판매중 등록한다. 제출 «전» 에 알려야
                    공급사가 모르는 채 자동견적 대상이 되지 않는다(V-11). */}
                <p
                  className="text-xs text-slate-600 leading-relaxed px-1"
                  data-testid="catalog-auto-register-notice"
                >
                  처음 견적하는 카탈로그 품목은 내 상품(판매중)으로 등록됩니다.
                  판매를 원하지 않는 품목은 «상품/단가 관리»에서 판매 상태를 꺼
                  주세요.
                </p>
              </div>

              {/* ✨ Bottom Submit Button (Hidden on Mobile, shown in Sticky Bar) */}
              <div className="mt-8 hidden md:flex justify-end">
                <Button
                  onClick={handleSave}
                  disabled={isSubmitting}
                  className="w-full sm:w-auto h-12 text-base font-semibold px-8 bg-primary hover:bg-primary-hover disabled:bg-primary/50"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      견적서 발송 중...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />위 내용으로 견적서 발송
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ✨ Mobile Sticky Bottom Action Bar */}
      <div className="md:hidden border-t border-gray-200 bg-white p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-20">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col">
            <span className="text-xs text-gray-500 font-medium">
              총 합계금액
            </span>
            <span className="text-lg font-semibold text-primary-text">
              {total.toLocaleString()}원
            </span>
          </div>
          <Button
            onClick={handleSave}
            disabled={isSubmitting}
            className="flex-1 h-12 text-base font-semibold bg-primary hover:bg-primary-hover disabled:bg-primary/50 shadow-lg shadow-primary/20"
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>처리 중</span>
              </div>
            ) : (
              <span>
                {props.mode === "create" ? "견적서 발송" : "수정 완료"}
              </span>
            )}
          </Button>
        </div>
      </div>

      <CatalogUpdateModal
        isOpen={showCatalogModal}
        items={changedItems}
        onConfirm={handleConfirmUpdate}
        onCancel={handleCancelUpdate}
      />
    </motion.div>
  );

  return containerContent;
}

/**
 * Format file size for display (replicated here or import from util)
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}
